import { noop } from 'lodash';
import { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  QUEST_STATUSES,
  type QuestAchievementType,
} from '@didaskill/domain/user-achievements/constants';
import dayjs from '@didaskill/services/dayjs';
import { Tile } from '@didaskill/ui/design-system';
import { questSubtitleMessages, questTitleMessages } from '@didaskill/ui/intl';

import { HIDDEN_TITLE, QUEST_TILE_VARIANTS_MAP } from './constants';
import { QuestTileIcon } from './QuestTileIcon';

export type QuestProgress = {
  completed: number;
  total: number;
};

export type QuestTileProps = {
  type: QuestAchievementType;
  progress?: QuestProgress;
  nbDaysLeft?: number;
  isCompleted?: boolean;
  completionDate?: string;
  isLocked?: boolean;
  availableAfterDays?: number;
  onClick?: () => void;
};

export const QuestTileBase = ({
  type,
  progress,
  isLocked = false,
  isCompleted = false,
  completionDate,
  nbDaysLeft = 0,
  availableAfterDays = 0,
  onClick = noop,
}: QuestTileProps) => {
  const intl = useIntl();

  const questStatus = useMemo(() => {
    if (isLocked) {
      return QUEST_STATUSES.LOCKED;
    }
    if (isCompleted) {
      return QUEST_STATUSES.COMPLETED;
    }
    return QUEST_STATUSES.IN_PROGRESS;
  }, [isCompleted, isLocked]);

  const questSubtitleValues = useMemo(() => {
    switch (questStatus) {
      case QUEST_STATUSES.LOCKED:
        return {
          count: availableAfterDays,
        };
      case QUEST_STATUSES.IN_PROGRESS:
        return {
          count: nbDaysLeft,
        };
      case QUEST_STATUSES.COMPLETED:
        return {
          date: dayjs(completionDate).format('DD/MM/YY'),
        };
      default:
        return null;
    }
  }, [availableAfterDays, nbDaysLeft, completionDate, questStatus]);

  return (
    <Tile
      variant={QUEST_TILE_VARIANTS_MAP[questStatus]}
      icon={<QuestTileIcon status={questStatus} progress={progress} />}
      isDisabled={isLocked}
      title={isLocked ? HIDDEN_TITLE : intl.formatMessage(questTitleMessages[type])}
      subtitle={
        questSubtitleValues.count
          ? intl.formatMessage(questSubtitleMessages[questStatus], questSubtitleValues)
          : null
      }
      onClick={onClick}
    />
  );
};

QuestTileBase.displayName = 'QuestTile';

export const QuestTile = memo(QuestTileBase);
